import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { Logo } from "../components/Logo";
import { CheckCircle, XCircle, Loader, Clock } from "lucide-react";
import { setPortalSession } from "../components/PortalGuard";

interface AccessData {
  firstName: string;
  lastName: string;
  email: string;
  expiresAt: string;
}

type State = "loading" | "valid" | "invalid" | "error";

export function AccessPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<State>("loading");
  const [access, setAccess] = useState<AccessData | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); setMessage("No access token provided."); return; }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);

    fetch(`/api/access/${token}`, { signal: controller.signal })
      .then(async (res) => {
        const data = await res.json();
        if (data.valid) {
          const expiresAt = data.expiresAt as string;
          setPortalSession({ token, firstName: data.firstName, lastName: data.lastName, email: data.email, expiresAt });
          setAccess({ firstName: data.firstName, lastName: data.lastName, email: data.email, expiresAt });
          setState("valid");
        } else {
          setMessage(data.message || "Access denied.");
          setState("invalid");
        }
      })
      .catch(() => {
        setMessage("Unable to verify access. Please try again.");
        setState("error");
      })
      .finally(() => clearTimeout(timeout));

    return () => controller.abort();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#101828] flex flex-col">
      <nav className="border-b border-white/10 px-8 py-4 flex items-center gap-3">
        <Logo className="w-16" white />
        <span className="text-white/60 text-sm">Leo Group · Secure Access</span>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">

          {state === "loading" && (
            <div className="text-center">
              <Loader className="w-10 h-10 text-white/40 animate-spin mx-auto mb-4" />
              <p className="text-white/60 text-sm">Verifying access token…</p>
            </div>
          )}

          {state === "valid" && access && (
            <div className="bg-[#1E2939] border border-white/10 rounded-lg p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h1 className="text-white text-xl font-semibold mb-2">Access Granted</h1>
              <p className="text-white/60 text-sm mb-4">
                Welcome, <span className="text-white font-medium">{access.firstName} {access.lastName}</span>.
                Your access has been verified.
              </p>

              {/* Session validity notice */}
              <div className="flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3 mb-5 text-left">
                <Clock className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-300/90 text-xs leading-relaxed">
                  Your portal session is valid for <strong>20 minutes</strong>. A timer will appear inside the portal — you can renew it at any time without signing in again.
                </p>
              </div>

              <div className="bg-[#101828] border border-white/10 rounded px-4 py-3 text-xs text-white/40 text-left mb-6">
                <span className="text-white/60">Verified as:</span> {access.email}
              </div>
              <button
                onClick={() => navigate("/overview")}
                className="w-full bg-[#7B1A1A] hover:bg-[#6b1515] text-white py-3 rounded font-medium text-sm transition-colors"
              >
                Enter Portal
              </button>
            </div>
          )}

          {(state === "invalid" || state === "error") && (
            <div className="bg-[#1E2939] border border-red-500/20 rounded-lg p-8 text-center">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h1 className="text-white text-xl font-semibold mb-2">Access Denied</h1>
              <p className="text-white/60 text-sm mb-6">{message}</p>
              <button
                onClick={() => navigate("/")}
                className="text-sm text-white/40 hover:text-white transition-colors"
              >
                Return to homepage
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
